<template>
  <div class="not-found">
    <div class="not-found-grid" aria-hidden="true"></div>
    <div class="not-found-glow not-found-glow-a" aria-hidden="true"></div>
    <div class="not-found-glow not-found-glow-b" aria-hidden="true"></div>

    <div class="not-found-content">
      <p ref="codeRef" class="not-found-code" aria-hidden="true">
        <span class="digit">4</span><span class="digit">0</span><span class="digit">4</span>
      </p>

      <div ref="bodyRef" class="not-found-body">
        <h1 class="not-found-title">Lost in the void</h1>
        <p class="not-found-message">
          The page you're looking for doesn't exist or may have moved.
        </p>
        <p class="not-found-path">
          Requested: <code>{{ route.path }}</code>
        </p>
      </div>

      <div ref="actionsRef" class="not-found-actions">
        <router-link to="/" class="btn btn-primary">
          <i class="fas fa-home"></i>
          Back home
        </router-link>
        <router-link to="/about" class="btn btn-ghost">About</router-link>
        <router-link to="/contact" class="btn btn-ghost">Contact</router-link>
      </div>

      <p ref="hintRef" class="not-found-hint">
        Try the nav above — or
        <a href="mailto:jerome@jeromejbrown.com" class="hint-link">say hello</a>
        if you think this is a bug.
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import { gsap } from 'gsap';

const route = useRoute();
const codeRef = ref(null);
const bodyRef = ref(null);
const actionsRef = ref(null);
const hintRef = ref(null);

let floatTween;

onMounted(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  gsap.from('.not-found-code .digit', {
    y: 80,
    opacity: 0,
    rotateX: -90,
    stagger: 0.12,
    duration: 0.9,
    ease: 'back.out(1.4)',
  });

  gsap.from(bodyRef.value, {
    y: 24,
    opacity: 0,
    duration: 0.7,
    delay: 0.35,
    ease: 'power3.out',
  });

  gsap.from(actionsRef.value?.children || [], {
    y: 16,
    opacity: 0,
    stagger: 0.08,
    duration: 0.5,
    delay: 0.55,
    ease: 'power2.out',
  });

  gsap.from(hintRef.value, {
    opacity: 0,
    duration: 0.5,
    delay: 0.85,
  });

  if (!reducedMotion) {
    floatTween = gsap.to('.not-found-code .digit', {
      y: '+=8',
      duration: 2.2,
      stagger: { each: 0.25, yoyo: true, repeat: -1 },
      ease: 'sine.inOut',
    });
  }
});

onUnmounted(() => {
  floatTween?.kill();
});
</script>

<style scoped>
.not-found {
  position: relative;
  min-height: calc(100vh - 4rem);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.25rem 4rem;
  overflow: hidden;
}

.not-found-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(45, 212, 191, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(45, 212, 191, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, black 20%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, black 20%, transparent 75%);
}

.not-found-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}

.not-found-glow-a {
  width: 320px;
  height: 320px;
  top: 10%;
  left: 15%;
  background: rgba(59, 130, 246, 0.12);
}

.not-found-glow-b {
  width: 280px;
  height: 280px;
  bottom: 15%;
  right: 10%;
  background: rgba(45, 212, 191, 0.1);
}

.not-found-content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 36rem;
}

.not-found-code {
  display: flex;
  justify-content: center;
  gap: 0.05em;
  margin: 0 0 1.5rem;
  font-size: clamp(5rem, 18vw, 9rem);
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.04em;
  perspective: 600px;
}

.digit {
  display: inline-block;
  background: linear-gradient(180deg, #ffffff 0%, #99f6e4 45%, #3b82f6 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 30px rgba(45, 212, 191, 0.25));
  animation: digit-glitch 4s infinite;
}

.digit:nth-child(2) {
  animation-delay: 0.15s;
}

.digit:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes digit-glitch {
  0%, 92%, 100% {
    transform: translate(0);
    opacity: 1;
  }
  93% {
    transform: translate(-2px, 1px);
    opacity: 0.85;
  }
  94% {
    transform: translate(2px, -1px);
    opacity: 1;
  }
  95% {
    transform: translate(-1px, 2px);
  }
}

.not-found-title {
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.75rem;
}

.not-found-message {
  font-size: 1.0625rem;
  color: #9ca3af;
  line-height: 1.6;
  margin: 0 0 1rem;
}

.not-found-path {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.not-found-path code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.8125rem;
  color: #5eead4;
  background: rgba(45, 212, 191, 0.08);
  border: 1px solid rgba(45, 212, 191, 0.18);
  padding: 0.15rem 0.5rem;
  border-radius: 0.375rem;
}

.not-found-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
  margin: 2rem 0 1.5rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  border-radius: 9999px;
  text-decoration: none;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.btn-primary {
  color: #0f172a;
  background: #2dd4bf;
}

.btn-primary:hover {
  background: #5eead4;
}

.btn-ghost {
  color: #d1d5db;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-ghost:hover {
  color: #fff;
  border-color: rgba(45, 212, 191, 0.35);
  background: rgba(45, 212, 191, 0.08);
}

.not-found-hint {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.hint-link {
  color: #2dd4bf;
  text-decoration: none;
  transition: color 0.2s ease;
}

.hint-link:hover {
  color: #5eead4;
}

@media (prefers-reduced-motion: reduce) {
  .digit {
    animation: none;
  }
}
</style>
